/**
 * Sample Environmental Features for San Juan, Argentina
 *
 * @version 1.0
 * @updated 2026-02-10
 * @module core/modules/environmental/data
 *
 * @description
 * Datos de ejemplo para probar el sistema de selección de features en el mapa.
 * Incluye árboles, parques, zonas críticas e intervenciones dentro del área de San Juan.
 *
 * @remarks
 * Bounding Box San Juan 2026:
 * - West: -68.769772°
 * - East: -68.400892°
 * - South: -31.716953°
 * - North: -31.403840°
 * - Centro: -68.585332, -31.560397
 */

import type { FeatureCollection, Point, Polygon } from 'geojson';

/**
 * Árboles urbanos - Points
 */
export const sampleTrees: FeatureCollection<Point> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      id: 'tree-001',
      geometry: {
        type: 'Point',
        coordinates: [-68.535, -31.537], // Centro de San Juan
      },
      properties: {
        type: 'tree',
        name: 'Jacarandá - Plaza 25 de Mayo',
        species: 'Jacaranda mimosifolia',
        height: 12.5,
        diameter: 0.45,
        health: 'Bueno',
        plantedDate: '2015-03-10',
        lastInspection: '2025-11-15',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'tree-002',
      geometry: {
        type: 'Point',
        coordinates: [-68.528, -31.540],
      },
      properties: {
        type: 'tree',
        name: 'Lapacho Rosado - Av. Libertador',
        species: 'Handroanthus impetiginosus',
        height: 8.3,
        diameter: 0.32,
        health: 'Excelente',
        plantedDate: '2018-09-22',
        lastInspection: '2025-10-20',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'tree-003',
      geometry: {
        type: 'Point',
        coordinates: [-68.542, -31.533],
      },
      properties: {
        type: 'tree',
        name: 'Fresno - Barrio Rivadavia',
        species: 'Fraxinus pennsylvanica',
        height: 15.2,
        diameter: 0.58,
        health: 'Regular',
        plantedDate: '2008-05-14',
        lastInspection: '2025-12-01',
        notes: 'Requiere poda urgente',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'tree-004',
      geometry: {
        type: 'Point',
        coordinates: [-68.548, -31.545],
      },
      properties: {
        type: 'tree',
        name: 'Álamo - Parque de Mayo',
        species: 'Populus nigra',
        height: 18.7,
        diameter: 0.72,
        health: 'Bueno',
        plantedDate: '2005-08-30',
        lastInspection: '2025-11-28',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'tree-005',
      geometry: {
        type: 'Point',
        coordinates: [-68.520, -31.550],
      },
      properties: {
        type: 'tree',
        name: 'Aguaribay - Ruta 40',
        species: 'Schinus molle',
        height: 10.1,
        diameter: 0.38,
        health: 'Excelente',
        plantedDate: '2020-04-15',
        lastInspection: '2025-12-05',
        moduleId: 'environmental',
      },
    },
  ],
};

/**
 * Parques y espacios verdes - Polygons
 */
export const sampleParks: FeatureCollection<Polygon> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      id: 'park-001',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-68.538, -31.535],
            [-68.532, -31.535],
            [-68.532, -31.539],
            [-68.538, -31.539],
            [-68.538, -31.535],
          ],
        ],
      },
      properties: {
        type: 'park',
        name: 'Plaza 25 de Mayo',
        area: 3.2, // hectáreas
        category: 'Plaza histórica',
        facilities: ['Bancos', 'Iluminación', 'Senderos', 'Fuente'],
        treeCount: 45,
        grassCoverage: 65, // porcentaje
        lastMaintenance: '2025-11-20',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'park-002',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-68.550, -31.542],
            [-68.545, -31.542],
            [-68.545, -31.548],
            [-68.550, -31.548],
            [-68.550, -31.542],
          ],
        ],
      },
      properties: {
        type: 'park',
        name: 'Parque de Mayo',
        area: 8.5,
        category: 'Parque urbano',
        facilities: ['Juegos infantiles', 'Cancha', 'Parrillas', 'Baños', 'Kiosco'],
        treeCount: 120,
        grassCoverage: 75,
        lastMaintenance: '2025-12-01',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'park-003',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-68.515, -31.548],
            [-68.510, -31.548],
            [-68.510, -31.552],
            [-68.515, -31.552],
            [-68.515, -31.548],
          ],
        ],
      },
      properties: {
        type: 'park',
        name: 'Plaza Hipólito Yrigoyen',
        area: 1.8,
        category: 'Plaza barrial',
        facilities: ['Bancos', 'Iluminación', 'Juegos'],
        treeCount: 28,
        grassCoverage: 55,
        lastMaintenance: '2025-10-15',
        moduleId: 'environmental',
      },
    },
  ],
};

/**
 * Zonas críticas de calor (Hotspots LST) - Polygons
 */
export const sampleHotspots: FeatureCollection<Polygon> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      id: 'hotspot-001',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-68.525, -31.555],
            [-68.518, -31.555],
            [-68.518, -31.560],
            [-68.525, -31.560],
            [-68.525, -31.555],
          ],
        ],
      },
      properties: {
        type: 'hotspot',
        name: 'Zona Industrial Este',
        severity: 'critical',
        avgLST: 42.3, // °C
        maxLST: 45.8,
        ndvi: 0.08,
        imperviousness: 85, // porcentaje
        population: 3200,
        vulnerablePopulation: 480, // Adultos mayores + niños
        shadeCoverage: 5,
        interventionStatus: 'planned',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'hotspot-002',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-68.562, -31.528],
            [-68.555, -31.528],
            [-68.555, -31.533],
            [-68.562, -31.533],
            [-68.562, -31.528],
          ],
        ],
      },
      properties: {
        type: 'hotspot',
        name: 'Barrio Comercial Centro',
        severity: 'high',
        avgLST: 39.7,
        maxLST: 42.1,
        ndvi: 0.12,
        imperviousness: 78,
        population: 5800,
        vulnerablePopulation: 720,
        shadeCoverage: 8,
        interventionStatus: 'active',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'hotspot-003',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-68.508, -31.565],
            [-68.502, -31.565],
            [-68.502, -31.570],
            [-68.508, -31.570],
            [-68.508, -31.565],
          ],
        ],
      },
      properties: {
        type: 'hotspot',
        name: 'Zona Residencial Sur',
        severity: 'medium',
        avgLST: 37.2,
        maxLST: 39.5,
        ndvi: 0.18,
        imperviousness: 65,
        population: 2100,
        vulnerablePopulation: 280,
        shadeCoverage: 12,
        interventionStatus: 'none',
        moduleId: 'environmental',
      },
    },
  ],
};

/**
 * Intervenciones planificadas/activas - Points
 */
export const sampleInterventions: FeatureCollection<Point> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      id: 'intervention-001',
      geometry: {
        type: 'Point',
        coordinates: [-68.558, -31.530],
      },
      properties: {
        type: 'intervention',
        name: 'Arbolado Av. Libertador - Tramo 1',
        category: 'tree-planting',
        status: 'active',
        startDate: '2025-10-01',
        endDate: '2026-03-31',
        budget: 450000,
        treesToPlant: 85,
        treesPlanted: 42,
        progress: 49,
        contractor: 'Green City SRL',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'intervention-002',
      geometry: {
        type: 'Point',
        coordinates: [-68.522, -31.557],
      },
      properties: {
        type: 'intervention',
        name: 'Techos Verdes - Zona Industrial',
        category: 'green-roof',
        status: 'planned',
        startDate: '2026-04-01',
        endDate: '2026-10-31',
        budget: 1200000,
        buildingsTarget: 12,
        buildingsCompleted: 0,
        progress: 0,
        contractor: 'EcoTech Construcciones',
        moduleId: 'environmental',
      },
    },
    {
      type: 'Feature',
      id: 'intervention-003',
      geometry: {
        type: 'Point',
        coordinates: [-68.546, -31.544],
      },
      properties: {
        type: 'intervention',
        name: 'Riego Inteligente - Parque de Mayo',
        category: 'irrigation',
        status: 'completed',
        startDate: '2025-06-01',
        endDate: '2025-09-30',
        budget: 280000,
        areaIrrigated: 8.5, // hectáreas
        waterSaved: 32, // porcentaje
        progress: 100,
        contractor: 'AquaSmart SA',
        moduleId: 'environmental',
      },
    },
  ],
};

/**
 * Todas las features combinadas
 */
export const allSampleFeatures = {
  trees: sampleTrees,
  parks: sampleParks,
  hotspots: sampleHotspots,
  interventions: sampleInterventions,
};
